<script setup lang="ts">
import { onMounted, ref } from "vue";
import RadioButton from "@/stories/buttons/RadioButton/RadioButton.vue";
import { toRefs } from "vue";
import SVG from "@/stories/displays/SVG/SVG.vue";
import type { SVGName } from "@/SVGs";

interface Props {
  icon?: string;
  svg?: SVGName;
  iconRight?: string;
  radio?: boolean;
  isActive?: boolean;
  textColor?: string;
  iconColor?: string;
  autoClose?: boolean;
  customClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  radio: false,
  isActive: false,
  textColor: "",
  autoClose: true,
  customClass: "",
});

const emit = defineEmits(["click"]);
const { icon } = toRefs(props);
const teleportId = ref<string | null>(null);
const buttonRef = ref();
const slotRef = ref();

const handleClick = () => {
  emit("click");
};

const waitForTheButton = () => {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (buttonRef?.value?.parentElement) {
        clearInterval(interval);
        resolve(0);
      }
    }, 10);
  });
};

onMounted(() => {
  waitForTheButton().then(() => {
    if (buttonRef?.value?.parentElement?.parentElement?.firstChild?.dataset) {
      teleportId.value =
        "#" +
        buttonRef?.value.parentElement.parentElement.firstChild.dataset
          .teleport;
    } else {
      teleportId.value = null;
    }
  });
});
</script>

<template>
  <button
    ref="buttonRef"
    type="button"
    @click="handleClick"
    class="b300 text-left px-2 my-1 py-2 transition-colors rounded-lg dropdown-link block w-full focus-visible-strong"
    :class="{
      'flex gap-3 items-center': icon || svg || radio,
      'justify-between': radio,
      'bg-neutral0 hover:bg-neutral50': !isActive,
      'bg-darkOcean100': isActive,
      'dropdown-trigger-no-close': !autoClose,
      [textColor]: true,
      [customClass]: true,
    }"
  >
    <slot name="left-icon">
      <SVG v-if="svg" :icon="svg" width="1.5rem" height="1.5rem"></SVG>
      <i
        v-else-if="icon"
        :class="{ [icon]: true, [textColor]: [] }"
        :style="{ color: iconColor }"
        class="min-w-5"
      />
    </slot>
    <div class="whitespace-nowrap flex-grow truncate" ref="slotRef">
      <slot></slot>
    </div>
    <slot name="right-icon">
      <i
        v-if="iconRight"
        :class="{ [iconRight]: true, [textColor]: [] }"
        class="min-w-5"
      />
    </slot>
    <div v-if="radio" class="pl-10">
      <RadioButton :active="isActive" :tabindex="-1" />
    </div>
  </button>
</template>
